<template>
  <transition name="modal-overlay">
    <div
        v-if="isOpen"
        class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-auto"
        @click="emitClose"
    >
      <transition name="modal-content">
        <div
            v-if="isOpen"
            class="bg-white rounded-lg shadow-xl max-w-7xl w-full mx-4 relative max-h-[90vh] flex flex-col"
            @click.stop
        >
          <!-- Header -->
          <div class="flex justify-between items-center p-6 flex-shrink-0">
            <h2 class="w-full text-lg text-center font-medium text-gray-700">
              TRANSACTION DETAILS
            </h2>
            <button
                class="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                @click="emitClose"
            >
              <svg
                  class="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
              >
                <path
                    d="M6 18L18 6M6 6l12 12"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                />
              </svg>
            </button>
          </div>

          <!-- Body -->
          <div class="p-6 overflow-y-auto flex-1 space-y-6">

            <!-- Receiver (Beneficiary) -->
            <div v-if="transaction.beneficiary" class="border p-4 rounded bg-gray-50 text-sm">
              <span class="font-bold text-gray-700 mb-2 block text-base">Receiver</span>

              <div class="flex justify-between">
                <span class="text-gray-600 font-medium">Name:</span>
                <span class="text-gray-800">{{ transaction.beneficiary.name || "-" }}</span>
              </div>

              <div class="flex justify-between">
                <span class="text-gray-600 font-medium">IBAN:</span>
                <span class="text-gray-800">{{ transaction.beneficiary.iban || "-" }}</span>
              </div>

              <div class="flex justify-between" v-if="transaction.beneficiary.account_number">
                <span class="text-gray-600 font-medium">Account Number:</span>
                <span class="text-gray-800">{{ transaction.beneficiary.account_number }}</span>
              </div>

              <div class="flex justify-between" v-if="transaction.beneficiary.type">
                <span class="text-gray-600 font-medium">Type:</span>
                <span class="text-gray-800">
  {{
                    transaction.beneficiary.type === 'european' ? 'SEPA Transfer' :
                        transaction.beneficiary.type === 'international' ? 'Swift Transfer' :
                            transaction.beneficiary.type
                  }}
</span>

              </div>

              <div class="flex justify-between" v-if="transaction.beneficiary.additional_info">
                <span class="text-gray-600 font-medium">Note:</span>
                <span class="text-gray-800">{{ transaction.beneficiary.additional_info }}</span>
              </div>
            </div>

            <!-- Transaction Fields (3-column grid) -->
            <div class="grid grid-cols-1 sm:grid-cols-4 gap-4 text-sm">
              <template v-for="(entry, index) in transactionPairs" :key="index">
                <div v-if="entry[0]">
                  <div class="flex justify-between border p-2 rounded bg-white">
                    <span class="text-gray-600 font-medium">{{ formatLabel(entry[0].key) }}</span>
                    <span class="text-gray-800">{{ formatValue(entry[0].value, entry[0].key, transaction) }}</span>
                  </div>
                </div>
                <div v-if="entry[1]">
                  <div class="flex justify-between border p-2 rounded bg-white">
                    <span class="text-gray-600 font-medium">{{ formatLabel(entry[1].key) }}</span>
                    <span class="text-gray-800">{{ formatValue(entry[1].value, entry[1].key, transaction) }}</span>
                  </div>
                </div>
              </template>
            </div>

          </div>

          <!-- Footer with Download Button -->
          <div class="p-6 flex justify-end flex-shrink-0 sticky top-0 bg-white z-10 border-t">
            <button
                @click="downloadPDF"
                v-if="transaction.transaction_type !== 'Balance Update'"
                class="bg-red-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg font-medium shadow">
              Download PDF
            </button>
          </div>

        </div>
      </transition>
    </div>
  </transition>
</template>

<script setup>
import {computed, defineProps, defineEmits} from "vue";
import jsPDF from "jspdf";

const props = defineProps({
  isOpen: {type: Boolean, required: true},
  transaction: {type: Object, required: true},
  account: {type: Object, required: true},
});

const emit = defineEmits(["close"]);
const emitClose = () => emit("close");

// فقط فیلدهای دارای مقدار را نگه می‌دارد و approval_status و beneficiary رو حذف می‌کند
const filledTransaction = computed(() => {
  const result = {};
  Object.entries(props.transaction).forEach(([key, value]) => {
    if (value !== null && value !== "" && value !== undefined && key !== "approval_status" && key !== "beneficiary") {
      result[key] = value;
    }
  });
  return result;
});

// گروه بندی دو تایی برای نمایش در grid
const transactionPairs = computed(() => {
  const entries = Object.entries(filledTransaction.value).map(([key, value]) => ({key, value}));
  const pairs = [];
  for (let i = 0; i < entries.length; i += 2) {
    pairs.push([entries[i], entries[i + 1] || null]);
  }
  return pairs;
});

const formatLabel = (label) => label.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

const formatValue = (val, key = null, transaction = {}) => {
  if (val === null || val === undefined || val === "") return "-";
  if (["value", "balance", "fee"].includes(key)) {
    const num = Number(val);
    if (!isNaN(num)) return num.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }) + " " + (transaction.currency || "");
  }
  if (typeof val === "string") {
    const isoDatePattern = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2})?/;
    if (isoDatePattern.test(val)) {
      const date = new Date(val);
      if (!isNaN(date.getTime())) return date.toLocaleString();
    }
  }
  if (val instanceof Date && !isNaN(val.getTime())) return val.toLocaleString();
  return val;
};


const downloadPDF = () => {
  if (!props.transaction) return;

  const doc = new jsPDF("p", "mm", "a4");
  const pageWidth = doc.internal.pageSize.getWidth();
  const leftMargin = 14;
  const rightMargin = 14;
  const contentWidth = pageWidth - leftMargin - rightMargin;

  const primaryColor = [241, 96, 8];
  const boxBgColor = [255, 250, 245];
  // const secondaryColor = [245, 245, 245];

  // هدر
  doc.setFillColor(...primaryColor);
  doc.rect(0, 0, pageWidth, 20, "F");
  doc.setFontSize(18);
  doc.setTextColor(255);
  doc.setFont("helvetica", "bold");
  doc.text("Transaction Confirmation", leftMargin, 12);

  let currentY = 30;

  // Sender
  // const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  const currentUser = props.selectedAccount || JSON.parse(localStorage.getItem("user") || "{}");
  doc.setFillColor(...boxBgColor);
  doc.setDrawColor(...primaryColor);
  doc.roundedRect(leftMargin, currentY, contentWidth / 2 - 4, 50, 2, 2, "FD");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(50);
  doc.text("Sender:", leftMargin + 3, currentY + 8);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  const safeValue = (val) => val ?? "-";

  doc.text(`Name: ${safeValue(currentUser.name)}`, leftMargin + 3, currentY + 16);
  doc.text(`Account Number: ${safeValue(props.account.account_number)}`, leftMargin + 3, currentY + 23);
  doc.text(`IBAN: ${safeValue(props.account.iban)}`, leftMargin + 3, currentY + 30);
  doc.text(`Swift Code: ${safeValue(props.account.swift)}`, leftMargin + 3, currentY + 37);
  doc.text(`Currency: ${safeValue(props.account.currency)}`, leftMargin + 3, currentY + 44);

  // Receiver
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(...primaryColor);
  doc.roundedRect(pageWidth / 2, currentY, contentWidth / 2 - 4, 50, 2, 2, "FD");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(50);
  doc.text("Receiver:", pageWidth / 2 + 3, currentY + 8);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);

  const beneficiary = props.transaction.beneficiary || {};
  doc.text(`Name:${beneficiary.name || "-"}`, pageWidth / 2 + 3, currentY + 16);
  doc.text(`IBAN: ${beneficiary.iban || "-"}`, pageWidth / 2 + 3, currentY + 23);
  if (beneficiary.account_number) doc.text(`Account Number: ${beneficiary.account_number}`, pageWidth / 2 + 3, currentY + 30);
  if (beneficiary.type) {
    let typeLabel = '';
    if (beneficiary.type === 'european') {
      typeLabel = 'SEPA Transfer';
    } else if (beneficiary.type === 'international') {
      typeLabel = 'Swift Transfer';
    } else {
      typeLabel = beneficiary.type; // fallback برای بقیه مقادیر
    }

    doc.text(`Type: ${typeLabel}`, pageWidth / 2 + 3, currentY + 37);
  }
  if (beneficiary.swift) doc.text(`Swift: ${beneficiary.swift}`, pageWidth / 2 + 3, currentY + 44);
  if (beneficiary.additional_info) doc.text(`Note: ${beneficiary.additional_info}`, pageWidth / 2 + 3, currentY + 44);
  currentY += 60;

  // Transaction Details Box
  // doc.setFillColor(...secondaryColor);
  // doc.roundedRect(leftMargin, currentY, contentWidth, 170, 2, 2, "FD");

  let boxStartY = currentY + 10;
  let y = boxStartY;
  const addRow = (label, value) => {
    const fontSize = 13; // اندازه فونت بزرگ‌تر
    const linePadding = 4; // فاصله بالای خط
    const infoLineHeight = 7; // ارتفاع هر خط متن
    const rightPadding = 10; // فاصله از سمت راست

    doc.setFontSize(fontSize);

    let displayLabel = label.replace(/\bId\b/gi, 'ID');
    // متن چپ (label)
    doc.setFont("helvetica", "bold");
    doc.setTextColor(50);
    doc.text(displayLabel, leftMargin + 10, y + fontSize / 2);

    // متن راست (value)
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80);
    const splitVal = doc.splitTextToSize(String(value || "-"), contentWidth - 70);
    const valueOffset = (splitVal.length - 1) * infoLineHeight / 2;
    doc.text(
        splitVal,
        leftMargin + contentWidth - rightPadding,
        y + fontSize / 2 - valueOffset,
        { align: "right" }
    );

    // خط زیر ردیف
    const lineY = y + splitVal.length * infoLineHeight + linePadding;
    if (!isNaN(lineY)) {
      doc.setDrawColor(200); // خاکستری پررنگ‌تر
      doc.setLineWidth(0.1);
      doc.line(leftMargin, lineY, leftMargin + contentWidth, lineY);
    }

    // فاصله بعدی
    y += splitVal.length * infoLineHeight + linePadding;
  };

  // const boxHeight = y - boxStartY + 10; // ارتفاع واقعی باکس بر اساس ردیف‌ها

// رسم باکس با ارتفاع داینامیک
//   doc.setFillColor(...secondaryColor);
//   doc.roundedRect(leftMargin, boxStartY - 10, contentWidth, boxHeight, 2, 2, "FD");


  Object.entries(props.transaction).forEach(([key, val]) => {
    if (val !== null && val !== "" && val !== undefined
        && key !== "approval_status"
        && key !== "beneficiary"
        && key !== "balance"
        && key !== "value_date"
        && key !== "created_at"
        && key !== "updated_at"
        && key !== "file_path"
    ) {
      addRow(formatLabel(key), formatValue(val, key, props.transaction));
    }
  });

  // Footer
  const pageHeight = doc.internal.pageSize.height;
  doc.setFontSize(9);
  doc.setTextColor(100);
  doc.text(`Generated on: ${new Date().toLocaleString()}`, leftMargin, pageHeight - 10);
  doc.text("© 2025 Bison Account. All rights reserved.", 120, pageHeight - 10);

  doc.save(`transaction_${props.transaction.id || "pending"}.pdf`);
};
</script>
